import { Component, ElementRef, ViewChild } from "@angular/core";
import { TooltipModule } from "@syncfusion/ej2-angular-popups";
import { ToastrService } from "ngx-toastr";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";
import { GeneralService } from "src/app/services/general.service";
import { LayoutService } from "src/app/services/layout.service";
import { EActionTypes, EToaster, EUserSecurityFunctionID } from "src/app/Shared/enum";
import { IUserPermissionHandler } from "src/app/Shared/model/CommonModel";
import { ILayoutPart } from "src/app/Shared/model/LayoutModel";
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent} from "src/app/Shared/shared-imports";
import { SharedHTMLSettingsDataService } from "src/app/Shared/sharedHTMLSettingsData.service";
import { UserPermissionHandlerDirective } from "src/app/Shared/userPermissionHandler.directive";
import Swal from "sweetalert2";
declare var $: any;

@Component({
  selector: "app-ops-layout-parts",
  imports: [ SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, UserPermissionHandlerDirective, TooltipModule],
  standalone: true,
  templateUrl: "./ops-layout-parts.html",
  styleUrl: "./ops-layout-parts.scss"
})
export class OpsLayoutParts {

  @ViewChild('layoutPartModal', { static: false }) layoutPartModal: ElementRef;
  @ViewChild('newPartButton', { static: false }) newPartButton: ElementRef;
  eNumActionTypes = EActionTypes;
  descriptionName: Subject<any> = new Subject();
  private destroy$ = new Subject<void>();
  public loaderIcon: Promise<boolean>;
  partsList: ILayoutPart[] = [];
  modalPartDescription = '';
  editRightFalseMessage = '';
  currentPartDescription: string;
  partDescriptionExists = false;
  isCollapsed = true;
  modalPartId = 0;
  editRight = false;
  viewPermissionHandlerData: IUserPermissionHandler = {FunctionId: EUserSecurityFunctionID.LayoutParts, Action: EActionTypes.VIEW};  
  createPermissionHandlerData: IUserPermissionHandler = {FunctionId: EUserSecurityFunctionID.LayoutParts, Action: EActionTypes.CREATE};
  deletePermissionHandlerData: IUserPermissionHandler = {FunctionId: EUserSecurityFunctionID.LayoutParts, Action: EActionTypes.DELETE};

  constructor(
    private layoutService: LayoutService,
    private toastr: ToastrService,
    private generalService: GeneralService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService
  ) { }

  ngOnInit() {
    this.getUserPermissionForLayoutParts();
    this.getLayoutParts();
    this.descriptionName.pipe(debounceTime(800), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((event) => {
        this.validatePartDescriptionExists(event);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUserPermissionForLayoutParts(): void {
    const userRightsForParts = this.generalService.getUserPermissionsForModule(EUserSecurityFunctionID.LayoutParts);
    this.editRight = userRightsForParts.EditRight;
    this.editRightFalseMessage = this.generalService.EditRightFalseMessage;
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  openModal() {
    const modal = this.layoutPartModal.nativeElement;
    $(modal).modal('show');
    modal.classList.add('show');
  }

  clickNewPart() {
    this.modalPartId = 0;
    this.modalPartDescription = '';
    this.partDescriptionExists = false;
  }

  onPartsRowSelected(event) {
    if (event.target.dataset && event.target.dataset.colindex == 0) {
      if (!this.editRight) {
        this.toastr.info(EToaster.MsgInfoNoRightToEdit + $localize`:@@Part:Part`, EToaster.TitleInfo, this.sharedHTMLSettings.ToasterConfig);
      } else {
        this.openModal();
        const data = event.data;
        this.modalPartId = data.PartId;
        this.modalPartDescription = data.PartDescription;
        this.currentPartDescription = data.PartDescription;
        this.partDescriptionExists = false;
      }
    }
  }

  validatePartDescriptionExists(event) {
    this.partDescriptionExists = false;
    if (event.target.value && ((this.modalPartId != 0 && this.currentPartDescription.toLocaleLowerCase() != event.target.value.toLocaleLowerCase().trim()) || this.modalPartId == 0)) {
      const existsIndex = this.partsList.findIndex(rec => rec.PartDescription.toLocaleLowerCase().trim() == event.target.value.toLocaleLowerCase().trim());
      if (existsIndex != -1) {
        this.partDescriptionExists = true;
      }
    }
  }

  clickSaveLayoutPart(): void {
    if ((this.modalPartId != 0 && this.currentPartDescription != this.modalPartDescription) || this.modalPartId == 0) {
      Swal.fire({
        title: $localize`:@@Confirm:Confirm`,
        text: $localize`:@@ConfirmSavePart:Are you sure you want to save this Layout Part?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: $localize`:@@Yes:Yes`,
        cancelButtonText: $localize`:@@No:No`
      }).then((result) => {
        if (result.isConfirmed) {
          this.saveDataLayoutPart();
        }
      });
    }
  }

  saveDataLayoutPart() {
    this.loaderIcon = Promise.resolve(true);
    const savePartsModel: ILayoutPart = {PartId: this.modalPartId, PartDescription: this.modalPartDescription, MarketId: 0, BuildingOrLayoutMapped: false };
    this.layoutService.saveLayoutParts(savePartsModel).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.loaderIcon = Promise.resolve(false);
        if (response.Success) {
          this.getLayoutParts();
          this.toastr.success(EToaster.MsgSuccessSave + $localize`:@@Part:Part`, EToaster.TitleSuccess, this.sharedHTMLSettings.ToasterConfig);
        } else {
          this.toastr.error( EToaster.MsgErrorSave + $localize`:@@Part:Part`, EToaster.TitleError, this.sharedHTMLSettings.ToasterConfig);
        }
      },
      error: (error) => {
        this.loaderIcon = Promise.resolve(false);
        this.toastr.error( EToaster.MsgErrorSave + $localize`:@@Part:Part`, EToaster.TitleError, this.sharedHTMLSettings.ToasterConfig);
      },
    });
  }

  deletePart(part: ILayoutPart): void {
    if (part.BuildingOrLayoutMapped) {
      Swal.fire({
        title: $localize`:@@Warning:Warning`,
        text: $localize`:@@Warning:You cannot delete this Part as there are Layouts or Building Dimensions assigned to it.`,
        icon: "warning",
        cancelButtonText: $localize`:@@Ok:Ok`,
      });
    } else {
      Swal.fire({
        title: $localize`:@@Confirm:Confirm`,
        text: $localize`:@@ConfirmDeletePart:Are you sure you want to delete this Layout Part?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: $localize`:@@Delete:Delete`,
        cancelButtonText: $localize`:@@Cancel:Cancel`,
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteLayoutPart(part.PartId);
        }
      });
    }
  }
  
  deleteLayoutPart(partId: number) {
    this.loaderIcon = Promise.resolve(true);
    this.layoutService.deleteLayoutPart(partId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.loaderIcon = Promise.resolve(false);
        if (response.Success) {
          this.toastr.success(EToaster.MsgSuccessDelete + $localize`:@@Part:Part`, EToaster.TitleSuccess, this.sharedHTMLSettings.ToasterConfig);
          this.getLayoutParts();
        } else {
          this.toastr.error( EToaster.MsgErrorDelete + $localize`:@@Part:Part`, EToaster.TitleError, this.sharedHTMLSettings.ToasterConfig);
        }
      },
      error: (error) => {
        this.loaderIcon = Promise.resolve(false);
        this.toastr.error( EToaster.MsgErrorDelete + $localize`:@@Part:Part`, EToaster.TitleError, this.sharedHTMLSettings.ToasterConfig);
      },
    });
  }

  getLayoutParts() {
    this.layoutService.getParts().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: ILayoutPart[]) => {
        this.loaderIcon = Promise.resolve(false);
        if (response) {
          this.partsList = response;
        }
      },
      error: (error) => {
        this.loaderIcon = Promise.resolve(false);
        this.toastr.error(  EToaster.MsgErrorFetch + $localize`:@@Parts:Parts`, EToaster.TitleError, this.sharedHTMLSettings.ToasterConfig);
      },
    });
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

}
